/** Pure printer-selection rules for Zebra label printing (no browser APIs). */

export type PrinterSource = "manual" | "auto";

const LABEL_PRINTER_RE = /zdesigner|zebra/i;

export function isLabelPrinterName(name: string | null | undefined): boolean {
  return !!name && LABEL_PRINTER_RE.test(name);
}

/** First label-printer match (zebra-virtual preferred), never a non-Zebra fallback. */
export function pickAutoLabelPrinter(printers: string[]): string | null {
  const matches = printers.filter(isLabelPrinterName);
  if (!matches.length) return null;
  return matches.find((n) => /zebra-virtual/i.test(n)) ?? matches[0];
}

export interface PrinterChoice {
  printer: string | null;
  source: PrinterSource | "none";
}

/**
 * Manual choices are always kept (even if not currently listed).
 * Otherwise auto-pick a label printer from the list, or nothing.
 */
export function resolvePrinterChoice(input: {
  printers: string[];
  stored: string | null;
  storedSource: PrinterSource | null;
}): PrinterChoice {
  if (input.stored && input.storedSource === "manual") {
    return { printer: input.stored, source: "manual" };
  }
  const auto = pickAutoLabelPrinter(input.printers);
  return auto ? { printer: auto, source: "auto" } : { printer: null, source: "none" };
}

/** Legacy stored values without a source: trust only label-printer names (as manual). */
export function migrateLegacyPrinterSource(
  printerName: string | null,
  source: unknown,
): { printerName: string | null; printerSource: PrinterSource | null } {
  if (source === "manual" || source === "auto") {
    return { printerName, printerSource: printerName ? source : null };
  }
  if (printerName && isLabelPrinterName(printerName)) {
    return { printerName, printerSource: "manual" };
  }
  return { printerName: null, printerSource: null };
}

export function needsNonZebraConfirmation(
  printer: string,
  confirmed: string[],
): boolean {
  return !isLabelPrinterName(printer) && !confirmed.includes(printer);
}

export class NoLabelPrinterError extends Error {
  constructor() {
    super("No Zebra printer found — choose one in Settings › Label Printing.");
    this.name = "NoLabelPrinterError";
  }
}
